import { FC } from 'react';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useDeleteVerificationVideo } from '@/_pages/interwill/verification/_pageModule/hooks/useDeleteVerificationVideo';
import { VIDEO_CONFIG } from '@/entities/interwills/hooks/useWebcamRecorder/config';
import { useIwRecordingStore } from '@/entities/interwills/stores/iwRecording.store';
import { media } from '@/shared/components';
import VideoPlayer from '@/shared/components/VideoPlayer';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { RecordingModalTabs } from '..';

type SavedPreviewTabProps = {
  videoUrl: string;
  setTab: (tab: RecordingModalTabs) => void;
};

export const SavedPreviewTab: FC<SavedPreviewTabProps> = ({ videoUrl, setTab }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'recordingModal' });

  const modals = useModalsStore();
  const deleteHandler = useDeleteVerificationVideo();
  const { recordingFor } = useIwRecordingStore();

  const onDelete = () => {
    modals.closeModal();
    modals.openModal({
      type: 'VideoDeletionConfirmModal',
      onDelete: async () => {
        await deleteHandler(recordingFor?.witnessId ?? undefined);
        modals.closeModal();
      },
    });
  };

  const onRerecord = () => {
    modals.closeModal();
    modals.openModal({
      type: 'VideoRerecordConfirmModal',
      onSubmit: async () => {
        await deleteHandler(recordingFor?.witnessId ?? undefined);
        setTab(RecordingModalTabs.RECORD);
        modals.closeModal();
      },
    });
  };

  return (
    <Container>
      <VideoWrapper>
        <VideoPlayerStyled
          options={{
            controls: true,
            muted: false,
            preload: 'auto',
            sources: [
              {
                src: videoUrl,
                type: VIDEO_CONFIG.videoContainer,
              },
            ],
            fill: true,
            enableSmoothSeeking: true,
          }}
        />
      </VideoWrapper>
      <Buttons>
        <ButtonIwStyled onClick={onDelete} type="text" icon={<Icon name="trash" size="s20" />}>
          {t('delete')}
        </ButtonIwStyled>
        <ButtonIw type="default" onClick={onRerecord}>
          {t('rerecord')}
        </ButtonIw>
      </Buttons>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-between;
`;

const VideoWrapper = styled.div`
  border-radius: 16px;
  overflow: auto;
  height: 100%;

  > * {
    height: 100%;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;

  ${media.iwMobile} {
    flex-direction: column-reverse;
    gap: 16px;
  }
`;

const ButtonIwStyled = styled(ButtonIw)`
  color: ${COLORS.white}!important;
`;

const VideoPlayerStyled = styled(VideoPlayer)`
  ${media.iwMobileOrLandscape} {
    position: absolute;
    inset: 0;

    .vjs-control-bar {
      margin-bottom: 160px;
    }

    .vjs-tech {
      object-fit: cover;
    }
  }
`;
